/**
 * arbi update       — update to latest version (shows changelog)
 * arbi update auto  — enable auto-update on login
 */

import { Command } from 'commander'
import { execSync } from 'node:child_process'
import { updateConfig } from '../config.js'
import { getLatestVersion, getCurrentVersion, showChangelog } from '../version-check.js'
import { success, error, dim, label } from '../output.js'
import { printJson } from '../helpers.js'

export function registerUpdateCommand(program: Command): void {
  const update = program
    .command('update')
    .description('Update ARBI CLI to the latest version (use --check to only probe)')
    .option('--check', 'Only report whether an update is available — never install anything')
    .option('--json', 'Output {current, latest, update_available} as JSON (implies --check)')
    .action((opts: { check?: boolean; json?: boolean }) => {
      const current = getCurrentVersion()
      // --json implies --check: an agent calling `arbi update --json` is
      // looking for a status probe, not an in-place reinstall.
      const checkOnly = Boolean(opts.check || opts.json)

      if (!checkOnly) {
        label('Current version:', current)
        console.log('Checking for updates...\n')
      }

      try {
        const latest =
          getLatestVersion(true) ||
          execSync('npm view @arbidocs/cli version', { encoding: 'utf8' }).trim()

        if (checkOnly) {
          // Non-mutating probe. Exit 0 if up to date, 1 if an update is
          // available — same convention as `git diff --exit-code` so a CI
          // step can branch with `arbi update --check || arbi update`.
          const updateAvailable = latest !== current
          if (opts.json) {
            printJson({ current, latest, update_available: updateAvailable })
          } else if (updateAvailable) {
            label('Current version:', current)
            label('Latest version:', latest)
            console.log('Update available. Run `arbi update` to install.')
          } else {
            label('Current version:', current)
            success('Already up to date.')
          }
          process.exit(updateAvailable ? 1 : 0)
        }

        if (latest === current) {
          success('Already up to date.')
          return
        }

        success(`New version available: ${latest}`)

        console.log('\nUpdating...\n')
        execSync('npm install -g @arbidocs/cli@latest', { stdio: 'inherit' })

        // Show what changed (reads from the newly installed package)
        showChangelog(current, latest)

        success(`\nUpdated to ${latest}.`)
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        if (opts.json) {
          printJson({ current, latest: null, update_available: null, error: msg })
          process.exit(1)
        }
        error(`Update failed: ${msg}`)
        error('\nYou can update manually with:')
        dim('  npm install -g @arbidocs/cli@latest')
        process.exit(1)
      }
    })

  update
    .command('auto')
    .description('Enable automatic updates on login')
    .action(() => {
      updateConfig({ autoUpdate: true })
      success('Auto-update enabled. ARBI CLI will update automatically on login.')
    })
}
